import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import RedoRoundedIcon from '@mui/icons-material/RedoRounded';
import {
  Box,
  ButtonBase,
  FormControl,
  FormHelperText,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { DateCalendar, DateRangeCalendar, LocalizationProvider } from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/pt-br';
import {
  forwardRef,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  RefAttributes,
  useEffect,
  useState,
} from 'react';
import type { MouseEvent } from 'react';
import { Controller, useFormContext as useReactHookFormContext } from 'react-hook-form';
import { useSiloRealFormContext } from '../base/use-form-context';
import { useFormFieldStyles } from './use-form-field-styles';
import * as HOOKS from '@siloreal:shared:hooks';

type FR<T, P> = ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>;

type CalendarFieldSx = {
  flex: number;
  minWidth: string;
};

type CalendarSide = 'start' | 'end';

export type CalendarInputValue = {
  startDate: string | null;
  endDate: string | null;
};

type Props = {
  tid: string;
  name: string;
  required?: boolean;
  label?: string;
  size?: 'medium' | 'large';
  disabled?: boolean;
  onChange?: (value: CalendarInputValue) => void;
};

const DATE_VALUE_FORMAT = 'YYYY-MM-DD';
const DATE_DISPLAY_FORMAT = 'DD/MM/YYYY';
const DAYJS_LOCALE_BY_LANGUAGE = {
  en: 'en',
  es: 'es',
  pt: 'pt-br',
} as const;
const GREEN = '#6BAA00';
const GREEN_DARK = '#4F8000';
const GREEN_SOFT = '#EFF8E7';
const GREEN_BORDER = '#CFE4B6';
const BLUE = '#22A7F0';
const BLUE_DARK = '#1C93D3';
const BLUE_SOFT = '#22A7F02A';
const BLUE_HOVER = '#22A7F042';
const BLUE_ACTIVE = '#22A7F05A';
const TEXT_COLOR = '#2F4319';
const MUTED_TEXT = '#757575';
const DISABLED_TEXT = '#A8A8A8';
const EMPTY_BORDER = '#BDBDBD';
const EMPTY_CORE = '#D8D8D8';
const DISABLED_BORDER = '#DADADA';
const DISABLED_CORE = '#CBCBCB';
const DISABLED_BACKGROUND = '#F4F4F4';

const EMPTY_VALUE: CalendarInputValue = {
  startDate: null,
  endDate: null,
};

const normalizeDateValue = (value: unknown): string | null => {
  if (!value) return null;
  if (
    typeof value !== 'string' &&
    typeof value !== 'number' &&
    !(value instanceof Date) &&
    !dayjs.isDayjs(value)
  ) {
    return null;
  }

  const date = dayjs(value as dayjs.ConfigType);
  return date.isValid() ? date.format(DATE_VALUE_FORMAT) : null;
};

const normalizeFieldValue = (value: unknown): CalendarInputValue => {
  if (!value || typeof value !== 'object') return { ...EMPTY_VALUE };

  const currentValue = value as Partial<CalendarInputValue>;

  return {
    startDate: normalizeDateValue(currentValue.startDate),
    endDate: normalizeDateValue(currentValue.endDate),
  };
};

const toDayjs = (value: string | null): Dayjs | null => {
  if (!value) return null;

  const date = dayjs(value);
  return date.isValid() ? date : null;
};

const toInputDate = (value: Dayjs | null): string | null => {
  return value?.isValid() ? value.format(DATE_VALUE_FORMAT) : null;
};

const formatDate = (value: string | null, locale: keyof typeof DAYJS_LOCALE_BY_LANGUAGE): string | null => {
  const date = toDayjs(value);
  return date ? date.locale(DAYJS_LOCALE_BY_LANGUAGE[locale]).format(DATE_DISPLAY_FORMAT) : null;
};

const getSummary = (value: CalendarInputValue, locale: keyof typeof DAYJS_LOCALE_BY_LANGUAGE): string => {
  const startDate = formatDate(value.startDate, locale);
  const endDate = formatDate(value.endDate, locale);

  if (startDate && endDate) return `${startDate} — ${endDate}`;
  if (startDate) return `Desde ${startDate}`;
  if (endDate) return `Hasta ${endDate}`;
  return '—';
};

const getDateDetail = (value: string | null, locale: keyof typeof DAYJS_LOCALE_BY_LANGUAGE): string => {
  const date = toDayjs(value);

  return date ? date.locale(DAYJS_LOCALE_BY_LANGUAGE[locale]).format('dddd, D MMMM YYYY') : '';
};

const getTimelineLineBackground = (value: CalendarInputValue, disabled: boolean): string => {
  const hasStartDate = Boolean(value.startDate);
  const hasEndDate = Boolean(value.endDate);

  if (disabled) return 'linear-gradient(90deg, transparent 0%, #D8D8D8 16%, #D8D8D8 84%, transparent 100%)';
  if (hasStartDate && hasEndDate) {
    return `linear-gradient(90deg, ${GREEN} 0%, #42D296 38%, ${BLUE} 50%, #42D296 62%, ${GREEN} 100%)`;
  }
  if (hasStartDate) {
    return `linear-gradient(90deg, ${GREEN} 0%, rgba(107, 170, 0, 0.48) 48%, rgba(189, 189, 189, 0.22) 100%)`;
  }
  if (hasEndDate) {
    return `linear-gradient(90deg, rgba(189, 189, 189, 0.22) 0%, rgba(107, 170, 0, 0.48) 52%, ${GREEN} 100%)`;
  }

  return 'linear-gradient(90deg, transparent 0%, #D4D4D4 18%, #D4D4D4 82%, transparent 100%)';
};

type CalendarEndpointProps = {
  tid: string;
  side: CalendarSide;
  selected: boolean;
  disabled: boolean;
  onClick: (event: MouseEvent<HTMLElement>) => void;
};

type EndpointHintProps = {
  side: CalendarSide;
  show: boolean;
};

const EndpointHint = ({ side, show }: EndpointHintProps) => {
  const i18n = HOOKS.useLocale();

  if (!show) return null;

  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'absolute',
        top: -8,
        left: side === 'start' ? 32 : 'auto',
        right: side === 'end' ? 32 : 'auto',
        zIndex: 1,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        color: '#8A8A8A',
        pointerEvents: 'none',
        transform: side === 'start' ? 'rotate(8deg)' : 'rotate(-8deg)',
        transformOrigin: side === 'start' ? 'left bottom' : 'right bottom',
      }}
    >
      {side === 'start' && <RedoRoundedIcon sx={{ fontSize: 15, transform: 'scaleX(-1) rotate(14deg)' }} />}

      <Typography
        variant="caption"
        sx={{
          fontSize: 9,
          fontWeight: 700,
          lineHeight: 1,
          color: 'inherit',
        }}
      >
        {i18n.t('temp.common.click_exclamation')}
      </Typography>

      {side === 'end' && <RedoRoundedIcon sx={{ fontSize: 15, transform: 'scaleY(1) rotate(14deg)' }} />}
    </Box>
  );
};

const CalendarEndpoint = ({ tid, side, selected, disabled, onClick }: CalendarEndpointProps) => {
  const i18n = HOOKS.useLocale();

  const label =
    side === 'start' ? i18n.t('temp.common.select_start_date') : i18n.t('temp.common.select_closure_date');
  const borderColor = disabled ? DISABLED_BORDER : selected ? GREEN : EMPTY_BORDER;
  const coreColor = disabled ? DISABLED_CORE : selected ? GREEN : EMPTY_CORE;
  const iconColor = disabled ? '#F0F0F0' : '#FFFFFF';

  return (
    <Tooltip title={disabled ? '' : label}>
      <Box
        component="span"
        sx={{
          position: 'absolute',
          top: 7,
          left: side === 'start' ? 0 : 'auto',
          right: side === 'end' ? 0 : 'auto',
          zIndex: 2,
          display: 'inline-flex',
        }}
      >
        <ButtonBase
          component="button"
          type="button"
          disabled={disabled}
          aria-label={label}
          data-testid={`${tid}-${side}-btn`}
          onClick={onClick}
          sx={{
            width: 44,
            height: 44,
            border: '1.5px solid',
            borderColor,
            borderRadius: '50%',
            backgroundColor: disabled ? DISABLED_BACKGROUND : '#FFFFFF',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
            boxShadow: selected && !disabled ? '0 8px 18px rgba(107, 170, 0, 0.18)' : 'none',
            '&:not(.Mui-disabled):hover': {
              borderColor: selected ? GREEN_DARK : '#9E9E9E',
              transform: 'translateY(-1px)',
              boxShadow: selected
                ? '0 10px 22px rgba(107, 170, 0, 0.24)'
                : '0 8px 18px rgba(117, 117, 117, 0.18)',
            },
            '&:not(.Mui-disabled):hover .calendar-endpoint-core': {
              backgroundColor: selected ? GREEN_DARK : '#BDBDBD',
              transform: 'scale(1.05)',
            },
            '&.Mui-focusVisible': {
              boxShadow: `0 0 0 3px rgba(107, 170, 0, 0.22)`,
            },
            '&.Mui-disabled': {
              cursor: 'not-allowed',
            },
          }}
        >
          <Box
            className="calendar-endpoint-core"
            sx={{
              width: 26,
              height: 26,
              borderRadius: '50%',
              backgroundColor: coreColor,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 160ms ease, transform 160ms ease',
            }}
          >
            <CalendarMonthOutlinedIcon sx={{ fontSize: 16, color: iconColor }} />
          </Box>
        </ButtonBase>
      </Box>
    </Tooltip>
  );
};

type DateSelectionLabelProps = {
  tid: string;
  side: CalendarSide;
  value: string | null;
  disabled: boolean;
  onClear: () => void;
};

const DateSelectionLabel = ({ tid, side, value, disabled, onClear }: DateSelectionLabelProps) => {
  const locale = HOOKS.useLocale().locale;
  const formattedDate = formatDate(value, locale);
  const dateDetail = getDateDetail(value, locale);
  const clearLabel = side === 'start' ? 'Borrar fecha de inicio' : 'Borrar fecha de cierre';
  const clearButtonPosition = side === 'start' ? { right: 14 } : { left: 14 };

  if (!formattedDate) return null;

  return (
    <Box
      data-testid={`${tid}-${side}-date`}
      sx={{
        position: 'absolute',
        top: 62,
        left: side === 'start' ? 0 : 'auto',
        right: side === 'end' ? 0 : 'auto',
        width: 'calc(50% - 4px)',
        minWidth: 0,
        display: 'block',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          minWidth: 0,
          display: 'grid',
          gap: 0.125,
          pt: 1,
          pr: side === 'start' && !disabled ? 1.25 : 1.25,
          pb: 0.75,
          pl: side === 'end' && !disabled ? 1.25 : 1.25,
          border: '1px solid',
          borderColor: disabled ? DISABLED_BORDER : GREEN_BORDER,
          borderRadius: '8px',
          backgroundColor: disabled ? DISABLED_BACKGROUND : '#FFFFFF',
          color: disabled ? DISABLED_TEXT : TEXT_COLOR,
          boxShadow: disabled ? 'none' : '0 4px 10px rgba(107, 170, 0, 0.12)',
          textAlign: side === 'end' ? 'right' : 'left',
          boxSizing: 'border-box',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          {formattedDate}
        </Typography>

        <Typography
          variant="caption"
          title={dateDetail}
          sx={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: disabled ? DISABLED_TEXT : MUTED_TEXT,
            fontSize: 11,
            lineHeight: 1.2,
          }}
        >
          {dateDetail}
        </Typography>
      </Box>

      {!disabled && (
        <IconButton
          type="button"
          size="small"
          aria-label={clearLabel}
          data-testid={`${tid}-${side}-clear-btn`}
          onClick={(event) => {
            event.stopPropagation();
            onClear();
          }}
          sx={{
            position: 'absolute',
            top: -7,
            ...clearButtonPosition,
            zIndex: 1,
            width: 22,
            height: 22,
            p: 0,
            color: 'common.white',
            backgroundColor: '#EA1818',
            boxShadow: '0 4px 10px rgba(234, 24, 24, 0.22)',
            transition: 'box-shadow 160ms ease',
            '&:hover': {
              backgroundColor: '#EA1818',
              boxShadow: '0 0 0 3px rgba(234, 24, 24, 0.14), 0 6px 14px rgba(234, 24, 24, 0.26)',
            },
          }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
        </IconButton>
      )}
    </Box>
  );
};

export const calendar: FR<HTMLDivElement, Props> = forwardRef((props, ref) => {
  const { tid, name = 'calendar', label = 'Fechas', required = false } = props;
  const { size, disabled = false, onChange } = props;
  const [singlePicker, setSinglePicker] = useState<{ side: CalendarSide; anchorEl: HTMLElement } | null>(
    null
  );
  const [rangeAnchorEl, setRangeAnchorEl] = useState<HTMLElement | null>(null);
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);
  const [showEndpointHints, setShowEndpointHints] = useState(false);

  const { verify } = useSiloRealFormContext();
  useEffect(() => verify.uniqueness(name), []);

  const {
    control,
    formState: { errors },
  } = useReactHookFormContext();

  const hasError = Boolean(errors[name]);
  const errorMessage = errors[name]?.message as string | undefined;
  const fieldSx = useFormFieldStyles({ size, base: { sm: 112, md: 168 } }) as CalendarFieldSx;

  const openSinglePicker = (side: CalendarSide, event: MouseEvent<HTMLElement>): void => {
    if (disabled) return;

    setSinglePicker({ side, anchorEl: event.currentTarget });
  };

  const closeSinglePicker = (): void => {
    setSinglePicker(null);
  };

  const openRangePicker = (event: MouseEvent<HTMLElement>): void => {
    if (disabled) return;

    setRangeAnchorEl(event.currentTarget);
  };

  const closeRangePicker = (): void => {
    setRangeAnchorEl(null);
  };

  const toggleTimeline = (shouldShowEndpointHints: boolean): void => {
    if (disabled) return;

    setIsTimelineOpen((current) => {
      const nextOpen = !current;

      setShowEndpointHints(nextOpen && shouldShowEndpointHints);
      return nextOpen;
    });
  };

  const i18n = HOOKS.useLocale();

  const locale = HOOKS.useLocale().locale;
  const adapterLocale = locale === 'pt' ? 'pt-BR' : locale;

  return (
    <FormControl
      ref={ref}
      sx={{
        ...fieldSx,
        maxWidth: '100%',
        minWidth: `min(100%, ${fieldSx.minWidth})`,
        boxSizing: 'border-box',
        display: 'grid',
        gap: 0,
      }}
      error={hasError}
      required={required}
      disabled={disabled}
    >
      <Controller
        name={name}
        control={control}
        defaultValue={{ ...EMPTY_VALUE }}
        rules={{
          validate: (value) => {
            if (disabled || !required) return true;

            const currentValue = normalizeFieldValue(value);
            return Boolean(currentValue.startDate || currentValue.endDate) || 'Seleccioná al menos una fecha';
          },
        }}
        render={({ field }) => {
          const value = normalizeFieldValue(field.value);
          const startDate = toDayjs(value.startDate);
          const endDate = toDayjs(value.endDate);
          const hasSelectedDate = Boolean(value.startDate || value.endDate);
          const hasCompleteRange = Boolean(value.startDate && value.endDate);
          const rangeOpen = Boolean(rangeAnchorEl);
          const singlePickerOpen = Boolean(singlePicker);

          const updateValue = (nextValue: CalendarInputValue): void => {
            field.onChange(nextValue);
            onChange?.(nextValue);
          };

          const selectSingleDate = (side: CalendarSide, date: Dayjs | null): void => {
            const selectedDate = toInputDate(date);
            const nextValue = { ...value };

            if (selectedDate) {
              setShowEndpointHints(false);
            }

            if (side === 'start') {
              nextValue.startDate = selectedDate;

              if (
                selectedDate &&
                nextValue.endDate &&
                dayjs(selectedDate).isAfter(dayjs(nextValue.endDate))
              ) {
                nextValue.endDate = null;
              }
            } else {
              nextValue.endDate = selectedDate;

              if (
                selectedDate &&
                nextValue.startDate &&
                dayjs(selectedDate).isBefore(dayjs(nextValue.startDate))
              ) {
                nextValue.startDate = null;
              }
            }

            updateValue(nextValue);
            field.onBlur();
            closeSinglePicker();
          };

          const clearDate = (side: CalendarSide): void => {
            updateValue({
              ...value,
              [side === 'start' ? 'startDate' : 'endDate']: null,
            });
            field.onBlur();
          };

          const clearValue = (): void => {
            updateValue({ ...EMPTY_VALUE });
            field.onBlur();
          };

          const updateRange = (nextRange: [Dayjs | null, Dayjs | null]): void => {
            const nextValue = {
              startDate: toInputDate(nextRange[0]),
              endDate: toInputDate(nextRange[1]),
            };

            if (nextValue.startDate || nextValue.endDate) {
              setShowEndpointHints(false);
            }

            updateValue(nextValue);

            if (nextValue.startDate && nextValue.endDate) {
              field.onBlur();
              closeRangePicker();
            }
          };

          return (
            <>
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: '1px solid',
                  borderColor: disabled
                    ? DISABLED_BORDER
                    : isTimelineOpen || hasSelectedDate
                      ? GREEN
                      : GREEN_BORDER,
                  borderRadius: isTimelineOpen ? '8px 8px 0 0' : '8px',
                  backgroundColor: disabled ? DISABLED_BACKGROUND : isTimelineOpen ? GREEN_SOFT : '#FFFFFF',
                  color: disabled ? DISABLED_TEXT : TEXT_COLOR,
                  textAlign: 'left',
                  overflow: 'hidden',
                  transition: 'border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease',
                  '&:hover': disabled
                    ? undefined
                    : {
                        borderColor: GREEN,
                        backgroundColor: GREEN_SOFT,
                      },
                }}
              >
                <ButtonBase
                  component="button"
                  type="button"
                  disabled={disabled}
                  data-testid={`${tid}-summary-btn`}
                  aria-label="Mostrar selector de fechas"
                  aria-expanded={isTimelineOpen}
                  onClick={() => toggleTimeline(!hasSelectedDate)}
                  sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    px: 1.25,
                    py: 0.9,
                    color: 'inherit',
                    textAlign: 'left',
                    '&.Mui-focusVisible': {
                      boxShadow: `inset 0 0 0 3px rgba(107, 170, 0, 0.22)`,
                    },
                  }}
                >
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                    <CalendarMonthOutlinedIcon
                      sx={{ fontSize: 20, color: disabled ? DISABLED_TEXT : GREEN, flex: '0 0 auto' }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          color: disabled ? DISABLED_TEXT : '#66735B',
                          lineHeight: 1.2,
                        }}
                      >
                        {label}
                        {required && (
                          <Box component="span" sx={{ color: '#EA1818', ml: 0.25 }}>
                            *
                          </Box>
                        )}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          display: 'block',
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: 700,
                          color: disabled ? DISABLED_TEXT : hasSelectedDate ? TEXT_COLOR : '#5C6A50',
                        }}
                      >
                        {getSummary(value, locale)}
                      </Typography>
                    </Box>
                  </Box>

                  <KeyboardArrowDownRoundedIcon
                    sx={{
                      fontSize: 22,
                      color: disabled ? DISABLED_TEXT : GREEN,
                      flex: '0 0 auto',
                      transform: isTimelineOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                      transition: 'transform 180ms ease',
                    }}
                  />
                </ButtonBase>

                {hasSelectedDate && !disabled && (
                  <IconButton
                    type="button"
                    size="small"
                    aria-label="Borrar filtro de fechas"
                    data-testid={`${tid}-clear-btn`}
                    onClick={(event) => {
                      event.stopPropagation();
                      clearValue();
                    }}
                    sx={{
                      alignSelf: 'stretch',
                      flex: '0 0 42px',
                      width: 42,
                      minWidth: 42,
                      height: 'auto',
                      p: 0,
                      borderLeft: '1px solid #F7C2C2',
                      borderRadius: 0,
                      color: '#EA1818',
                      backgroundColor: '#FDE8E8',
                      transition: 'box-shadow 160ms ease',
                      '&:hover': {
                        backgroundColor: '#FDE8E8',
                        boxShadow: '0 0 0 3px rgba(234, 24, 24, 0.12)',
                      },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                )}
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateRows: isTimelineOpen ? '1fr' : '0fr',
                  opacity: isTimelineOpen ? 1 : 0,
                  transform: isTimelineOpen ? 'translateY(0)' : 'translateY(-4px)',
                  transition: 'grid-template-rows 180ms ease, opacity 180ms ease, transform 180ms ease',
                }}
              >
                <Box
                  sx={{
                    overflow: 'hidden',
                    minWidth: 0,
                  }}
                >
                  <Box
                    data-testid={tid}
                    sx={{
                      width: '100%',
                      maxWidth: '100%',
                      minWidth: 0,
                      boxSizing: 'border-box',
                      p: 1,
                      pt: 2,
                      border: '1px solid',
                      borderTop: 0,
                      borderColor: hasError ? 'error.main' : disabled ? DISABLED_BORDER : '#E2EAD9',
                      borderRadius: '0 0 8px 8px',
                      backgroundColor: disabled ? '#FAFAFA' : '#FFFFFF',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        minWidth: 0,
                        height: hasSelectedDate ? 108 : 58,
                        transition: 'height 180ms ease',
                      }}
                    >
                      <ButtonBase
                        component="button"
                        type="button"
                        disabled={disabled}
                        data-testid={`${tid}-range-btn`}
                        onClick={openRangePicker}
                        sx={{
                          position: 'absolute',
                          top: -4,
                          left: '50%',
                          zIndex: 2,
                          transform: 'translateX(-50%)',
                          minWidth: 62,
                          height: 24,
                          px: 1.25,
                          borderRadius: '8px',
                          border: '1px solid',
                          borderColor: disabled
                            ? DISABLED_BORDER
                            : hasCompleteRange
                              ? rangeOpen
                                ? BLUE_DARK
                                : BLUE
                              : EMPTY_BORDER,
                          backgroundColor: disabled
                            ? DISABLED_BACKGROUND
                            : hasCompleteRange
                              ? rangeOpen
                                ? BLUE_ACTIVE
                                : BLUE_SOFT
                              : '#F6F6F6',
                          color: disabled
                            ? DISABLED_TEXT
                            : hasCompleteRange
                              ? rangeOpen
                                ? BLUE_DARK
                                : BLUE
                              : MUTED_TEXT,
                          fontSize: 12,
                          fontWeight: 700,
                          lineHeight: 1,
                          textTransform: 'lowercase',
                          transition:
                            'border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease',
                          '&:not(.Mui-disabled):hover': {
                            borderColor: hasCompleteRange ? BLUE_DARK : '#9E9E9E',
                            backgroundColor: hasCompleteRange
                              ? rangeOpen
                                ? BLUE_ACTIVE
                                : BLUE_HOVER
                              : '#EFEFEF',
                            color: hasCompleteRange ? BLUE_DARK : MUTED_TEXT,
                            boxShadow: hasCompleteRange
                              ? '0 4px 10px rgba(34, 167, 240, 0.16)'
                              : '0 4px 10px rgba(117, 117, 117, 0.12)',
                          },
                        }}
                      >
                        {i18n.t('temp.common.range')}
                      </ButtonBase>

                      <Box
                        aria-hidden="true"
                        sx={{
                          position: 'absolute',
                          left: 22,
                          right: 22,
                          top: 29,
                          height: 3,
                          borderRadius: 999,
                          background: getTimelineLineBackground(value, disabled),
                        }}
                      />

                      <EndpointHint side="start" show={!disabled && showEndpointHints} />
                      <EndpointHint side="end" show={!disabled && showEndpointHints} />

                      <CalendarEndpoint
                        tid={tid}
                        side="start"
                        selected={Boolean(value.startDate)}
                        disabled={disabled}
                        onClick={(event) => openSinglePicker('start', event)}
                      />

                      <CalendarEndpoint
                        tid={tid}
                        side="end"
                        selected={Boolean(value.endDate)}
                        disabled={disabled}
                        onClick={(event) => openSinglePicker('end', event)}
                      />

                      <DateSelectionLabel
                        tid={tid}
                        side="start"
                        value={value.startDate}
                        disabled={disabled}
                        onClear={() => clearDate('start')}
                      />

                      <DateSelectionLabel
                        tid={tid}
                        side="end"
                        value={value.endDate}
                        disabled={disabled}
                        onClear={() => clearDate('end')}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Popover
                open={singlePickerOpen}
                anchorEl={singlePicker?.anchorEl ?? null}
                onClose={closeSinglePicker}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 1,
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: GREEN_BORDER,
                      boxShadow: '0 18px 44px rgba(47, 67, 25, 0.18)',
                    },
                  },
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
                  <DateCalendar
                    value={singlePicker?.side === 'start' ? startDate : endDate}
                    onChange={(nextDate) => {
                      if (!singlePicker) return;
                      selectSingleDate(singlePicker.side, nextDate);
                    }}
                    sx={{
                      '& .MuiPickersDay-root.Mui-selected': {
                        backgroundColor: GREEN,
                        '&:hover': {
                          backgroundColor: GREEN_DARK,
                        },
                      },
                      '& .MuiPickersDay-root.MuiPickersDay-today': {
                        borderColor: GREEN,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Popover>

              <Popover
                open={rangeOpen}
                anchorEl={rangeAnchorEl}
                onClose={closeRangePicker}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 1,
                      maxWidth: 'calc(100vw - 24px)',
                      overflowX: 'auto',
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: GREEN_BORDER,
                      boxShadow: '0 18px 44px rgba(47, 67, 25, 0.18)',
                    },
                  },
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
                  <Box sx={{ p: 1, minWidth: { xs: 320, sm: 620 } }}>
                    <DateRangeCalendar
                      calendars={2}
                      value={[startDate, endDate]}
                      onChange={updateRange}
                      sx={{
                        '& .MuiPickersDay-root.Mui-selected': {
                          backgroundColor: GREEN,
                          '&:hover': {
                            backgroundColor: GREEN_DARK,
                          },
                        },
                        '& .MuiDateRangePickerDay-rangeIntervalDayHighlight': {
                          backgroundColor: 'rgba(107, 170, 0, 0.16)',
                        },
                        '& .MuiDateRangePickerDay-rangeIntervalDayPreview': {
                          borderColor: GREEN,
                        },
                        '& .MuiPickersDay-root.MuiPickersDay-today': {
                          borderColor: GREEN,
                        },
                      }}
                    />
                  </Box>
                </LocalizationProvider>
              </Popover>
            </>
          );
        }}
      />
      <FormHelperText sx={{ ml: 0, mt: 0.75 }}>{errorMessage}</FormHelperText>
    </FormControl>
  );
});

calendar.displayName = 'SiloReal-Form-Field-Calendar';
